import Footer from "../footer/footer";
import Navbar from "../navbar/navbar";
import { Helmet } from "react-helmet";
import React from "react";
import { 
  FaIndustry, 
  FaTools, 
  FaWater, 
  FaFlask,
  FaCogs,
  FaChartLine,
  FaShieldAlt,
  FaHeadset,
  FaCubes,
  FaCheck
} from "react-icons/fa";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import { FaOilWell } from "react-icons/fa6";

// Import all valve images
import safety from "../../assets/safty-valve.jpg";
import brass from "../../assets/Brass Valve.png";
import gate from "../../assets/Gate Valve.png";
import globe from "../../assets/Globe Valve.png";
import cryogenic from "../../assets/Cryogenic Valve.png";
import ball from "../../assets/Ball Valve.png";
import pressure from "../../assets/Pressure Seal Valve.png";
import check from "../../assets/Check Valve.png";
import solenoid from "../../assets/Solenoid Valve.png";
import needle from "../../assets/Needle Valve.png";
import butterfly from "../../assets/Butterfly Valve.png";
import plug from "../../assets/Plug Valve.png";
import piston from "../../assets/Piston Valve.png";
import control from "../../assets/Control Valves.png";
import knife from "../../assets/Knife Gate Valve.png";
import Banner from "../../assets/valves.png";

const Valves: React.FC = () => {
  const valveProducts = [
    {
      name: "Safety Valve",
      description: "Designed to open and relieve excess pressure from vessels or equipment and to reclose after normal conditions have been restored.",
      image: safety,
      specs: [
        "Set Pressure: 0.5 to 300 bar",
        "Temperature Range: -196°C to 550°C",
        "Materials: Carbon Steel, Stainless Steel",
        "Certifications: ASME, PED, API"
      ],
      details: "Our safety valves feature spring-loaded mechanisms with corrosion-resistant trim for reliable overpressure protection. Designed with blowdown adjustment and optional lifting mechanisms, they provide critical safety in boilers, pressure vessels, and steam systems."
    },
    {
      name: "Brass Valve",
      description: "Highly corrosion-resistant with exceptionally low lead levels and malleable construction for various applications.",
      image: brass,
      specs: [
        "Pressure Rating: PN16 to PN40",
        "Connection Types: Threaded, Flanged",
        "Materials: DZR Brass, Gunmetal",
        "Standards: BS EN, DIN, ANSI"
      ],
      details: "Manufactured from lead-free dezincification-resistant brass, our valves offer superior corrosion resistance in water systems. With precision-machined components and PTFE seals, they ensure leak-free operation in plumbing, HVAC, and industrial water applications."
    },
    {
      name: "Gate Valve",
      description: "Opens by lifting a barrier (gate) out of the fluid path, requiring minimal space and offering unrestricted flow when fully open.",
      image: gate,
      specs: [
        "Size Range: 1/2\" to 48\"",
        "Pressure Class: 150# to 2500#",
        "Seat Design: Resilient, Metal-to-Metal",
        "Operation: Handwheel, Gear, Actuated"
      ],
      details: "Our gate valves feature wedge designs with self-adjusting seats for zero-leakage shutoff. With optional by-pass systems and extended bonnets for cryogenic service, they're ideal for isolation in pipelines, refineries, and power plants."
    },
    {
      name: "Globe Valve",
      description: "Linear motion valve used to stop, start, and regulate fluid flow with precise control capabilities.",
      image: globe,
      specs: [
        "Flow Characteristics: Equal/Linear Percentage",
        "Leakage Class: ANSI IV, V, VI",
        "Trim Options: Standard, Low Noise, Anti-Cavitation",
        "Bonnet Design: Bolted, Pressure Seal"
      ],
      details: "Engineered with parabolic disc design and balanced trim, our globe valves provide precise throttling control with minimal cavitation. Featuring renewable seat rings and guided stem movement, they offer long service life in steam, oil, and gas applications."
    },
    {
      name: "Cryogenic Valve",
      description: "Engineered to handle extremely low temperatures and high-pressure environments for specialized applications.",
      image: cryogenic,
      specs: [
        "Temperature Range: -196°C to +80°C",
        "Body Materials: Stainless Steel 316L, Monel",
        "Extended Bonnet: 150mm to 1000mm",
        "Seat Leakage: Class VI"
      ],
      details: "Designed with extended bonnets to prevent seat freezing, our cryogenic valves maintain reliable operation in LNG, liquid nitrogen, and oxygen systems. With special low-temperature packing and fire-safe designs, they ensure safety in critical cryogenic applications."
    },
    {
      name: "Ball Valve",
      description: "Quarter-turn valve using a hollow, perforated pivoting ball to control flow with quick operation.",
      image: ball,
      specs: [
        "Design: Floating/Trunnion Mounted",
        "Port: Full/Reduced Bore",
        "Fire-Safe: API 607/6FA",
        "End Connections: Flanged, Threaded, Welded"
      ],
      details: "Our ball valves feature chrome-plated or PTFE-coated balls with anti-blowout stems for reliable shutoff. Available in three-piece design for easy maintenance and V-port configuration for control applications, they're suitable for oil, gas, and chemical services."
    },
    {
      name: "Pressure Seal Valve",
      description: "Offers superior sealing performance in high-pressure applications compared to conventional designs.",
      image: pressure,
      specs: [
        "Pressure Class: 900# to 4500#",
        "Temperature Range: -29°C to 593°C",
        "Body Design: Pressure-Seal Bonnet",
        "Materials: Carbon Steel, Alloy Steel"
      ],
      details: "Utilizing pressure-energized sealing technology, our valves provide enhanced sealing at high pressures. With integral seat rings and forged construction, they're ideal for power generation, boiler feed, and high-pressure steam applications."
    },
    {
      name: "Check Valve",
      description: "Prevents backflow in pipelines allowing fluid to flow freely in one direction only.",
      image: check,
      specs: [
        "Types: Swing, Lift, Dual Plate, Tilting Disc",
        "End Connections: Wafer, Lug, Flanged",
        "Materials: Carbon Steel, Duplex, Hastelloy",
        "Certifications: API 594, API 6D"
      ],
      details: "Our check valves feature spring-assisted closure for rapid response and water hammer prevention. With streamlined flow paths and renewable seat components, they minimize pressure drop while providing reliable backflow prevention."
    },
    {
      name: "Solenoid Valve",
      description: "Electrically operated valve for precise control of gas or liquid flow in automated systems.",
      image: solenoid,
      specs: [
        "Voltage: 12VDC to 240VAC",
        "Orifice Size: 1/8\" to 2\"",
        "Media: Air, Water, Oil, Gas",
        "Function: 2/2-Way, 3/2-Way, 5/2-Way"
      ],
      details: "Designed with encapsulated coils and IP65/IP67 protection, our solenoid valves offer reliable operation in harsh environments. Featuring low power consumption and fast response times, they're ideal for automation, irrigation, and pneumatic systems."
    },
    {
      name: "Needle Valve",
      description: "Provides fine control and regulation of water flow and pressure with precise adjustment capabilities.",
      image: needle,
      specs: [
        "Flow Control: Precision Metering",
        "Pressure Rating: Up to 6000 psi",
        "Stem Tip: Hardened Stainless Steel",
        "Connection: NPT, BSPP, SAE"
      ],
      details: "Our needle valves feature micro-fine threads and tapered points for precise flow control. With calibrated handles and bubble-tight shutoff, they're essential for instrumentation, sampling systems, and hydraulic control applications."
    },
    {
      name: "Butterfly Valve",
      description: "Quarter-turn rotational valve with a disc mounted on rotating shaft for efficient flow control.",
      image: butterfly,
      specs: [
        "Disc Design: Eccentric, Concentric",
        "Seat Materials: EPDM, PTFE, Metal",
        "Operation: Lever, Gear, Actuator",
        "Size Range: 2\" to 120\""
      ],
      details: "Featuring high-performance eccentric disc design, our butterfly valves provide bubble-tight shutoff with low operating torque. With wafer, lug, and double-flanged configurations, they offer economical flow control in large diameter piping systems."
    },
    {
      name: "Plug Valve",
      description: "Features cylindrical or conically tapered plugs rotated within the valve body to control flow.",
      image: plug,
      specs: [
        "Port Types: Rectangular, Round",
        "Lubrication: Lubricated, Non-Lubricated",
        "Fire-Safe: API 607",
        "Pressure Rating: 150# to 2500#"
      ],
      details: "Our plug valves feature Teflon-sleeved or lubricated designs for smooth operation. With multi-port configurations and full bore openings, they provide versatile flow direction control in refining, chemical, and slurry applications."
    },
    {
      name: "Piston Valve",
      description: "Controls fluid motion along tubes or pipes using the linear motion of a piston within a cylinder.",
      image: piston,
      specs: [
        "Flow Capacity: Cv 0.01 to 250",
        "Pressure Rating: ANSI 150 to 2500",
        "Seal Materials: PTFE, Graphite, Elastomer",
        "Actuation: Manual, Pneumatic, Hydraulic"
      ],
      details: "Designed with balanced pistons and renewable seals, our piston valves provide precise control in high-pressure applications. Featuring low friction and minimal hysteresis, they're ideal for steam, gas, and hydraulic systems requiring accurate modulation."
    },
    {
      name: "Control Valves",
      description: "Regulates fluid flow by varying passage size as directed by a controller for precise process control.",
      image: control,
      specs: [
        "Characterization: Linear, Equal%, Quick Open",
        "Leakage Class: ANSI II to VI",
        "Actuator: Pneumatic, Electric, Hydraulic",
        "Positioner: Analog, Smart Digital"
      ],
      details: "Our control valves feature cage-guided trim with noise attenuation and anti-cavitation options. With digital positioners and HART communication, they provide precise process control with advanced diagnostics for predictive maintenance."
    },
    {
      name: "Knife Gate Valve",
      description: "Designed for on-off/isolation services in systems with high suspended solids content using a sharpened blade.",
      image: knife,
      specs: [
        "Blade Design: Single/Double Shear",
        "Seal System: Resilient, Metal Seated",
        "Size Range: 2\" to 96\"",
        "Pressure Rating: ANSI 125 to 300"
      ],
      details: "Featuring heavy-duty knife-edged gates and elastomer-lined bodies, our knife gate valves cut through fibrous and abrasive slurries. With fail-safe actuators and optional purge connections, they're ideal for pulp & paper, mining, and wastewater applications."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Industrial Valves Manufacturer & Exporter - Gate, Ball, Globe, Check, Butterfly Valves | City Cat International</title>
        <meta 
          name="description" 
          content="City Cat International – Leading manufacturer & exporter of industrial valves including Gate, Ball, Globe, Check & Butterfly types for oil, gas, chemical & power industries." 
        />
        <meta 
          name="keywords" 
          content="industrial valves manufacturer, gate valve exporter, ball valve supplier India, globe valve manufacturer, butterfly valve exporter, check valves supplier, industrial valve exporter India, City Cat International, oil and gas valve manufacturer" 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.oilandgasindustrialequipmentsupply.com/industrial-valves" />
        
        <meta property="og:title" content="Premium Industrial Valves Manufacturer & Global Supplier" />
        <meta property="og:description" content="Exporting API-certified valves to 40+ countries. 25+ years expertise in critical flow control solutions for oil & gas, chemical, and power industries." />
        <meta property="og:image" content="https://www.oilandgasindustrialequipmentsupply.com/assets/valves-banner.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Valves for Process Control | City Cat International" />
        <meta name="twitter:description" content="Global supplier of API-certified valves for refineries, pipelines, and chemical plants. Request technical specs." />
        
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "ProductPage",
            "name": "Industrial Valves",
            "description": "Manufacturer and exporter of premium industrial valves for global operations",
            "url": "https://www.oilandgasindustrialequipmentsupply.com/industrial-valves",
            "image": "https://www.oilandgasindustrialequipmentsupply.com/assets/valves-banner.jpg",
            "brand": {
              "@type": "Brand",
              "name": "City Cat International"
            },
            "manufacturer": {
              "@type": "Organization",
              "name": "City Cat International"
            }
          }
          `}
        </script>
      </Helmet>
      <Navbar />
      
      {/* Hero Banner */}
      <div className="relative bg-gray-900 mt-24">
          <img 
              src={Banner} 
              alt="Industrial Valves for Process Control" 
              className="w-full h-96 object-cover opacity-70"
          />
          <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Precision Flow Control Solutions
              </h1>
              <p className="text-xl text-white max-w-3xl">
                  Premium Gate, Globe, Ball, Check & Specialized Valves for Industrial Applications
              </p>
          </div>
      </div>

      {/* Industries Served Section */}
      <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Valves for Critical Industries
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
                  {[
                      { icon: <FaOilWell />, name: "Oil & Gas" },
                      { icon: <FaWater />, name: "Water Treatment" },
                      { icon: <FaIndustry />, name: "Chemical" },
                      { icon: <FaTools />, name: "Power Generation" },
                      { icon: <FaFlask />, name: "Pharmaceutical" },
                      { icon: <FaOilWell />, name: "Marine & Offshore" }
                  ].map((industry, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                          <div className="text-rose-600 text-3xl mb-3 flex justify-center">
                              {industry.icon}
                          </div>
                          <h3 className="font-semibold text-gray-800">{industry.name}</h3>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Comprehensive Solutions Section */}
      <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Valve Engineering Solutions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
                  {[
                      { 
                          icon: <FaCogs className="text-4xl mb-4" />,
                          title: "Custom Engineering",
                          desc: "Specialized designs for unique process conditions"
                      },
                      { 
                          icon: <FaChartLine className="text-4xl mb-4" />,
                          title: "Performance Guarantee",
                          desc: "Tested to API, ANSI, and ASME standards"
                      },
                      { 
                          icon: <FaShieldAlt className="text-4xl mb-4" />,
                          title: "Safety Compliance",
                          desc: "Fire-safe, SIL-rated, and fugitive emission certified"
                      },
                      { 
                          icon: <FaHeadset className="text-4xl mb-4" />,
                          title: "Technical Support",
                          desc: "Valve selection and application engineering"
                      },
                      { 
                          icon: <FaCubes className="text-4xl mb-4" />,
                          title: "Global Logistics",
                          desc: "Export packaging and worldwide shipping"
                      }
                  ].map((feature, index) => (
                      <div key={index} className="bg-gray-50 p-6 rounded-lg text-center">
                          <div className="text-rose-600 flex justify-center">{feature.icon}</div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                          <p className="text-gray-700">{feature.desc}</p>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      <div className="bg-white">
          <h2 className="text-center text-gray-800 text-4xl lg:text-5xl font-bold leading-tight pt-16">
             Valve Product Range
          </h2>
          <div className="my-4 mx-auto w-16 h-2 border-4 border-rose-600 mb-20"></div>

          {/* Valve Product Sections */}
          {valveProducts.map((valve, index) => (
            <section key={index} data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}>
              <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
                  <div className={`relative h-64 overflow-hidden rounded-lg sm:h-80 lg:h-full ${index % 2 === 0 ? "lg:order-last" : "lg:order-first"}`}>
                    <img
                      alt={`${valve.name} for industrial applications`}
                      src={valve.image}
                      className="absolute inset-0 h-full w-full object-contain" />
                  </div>
                  <div className="lg:py-16">
                    <h2 className="text-4xl text-gray-800 text-center font-bold sm:text-4xl">{valve.name}</h2>
                    
                    <div className="my-6">
                      <h3 className="text-xl font-semibold text-rose-600 mb-2">Key Specifications:</h3>
                      <ul className="list-disc pl-5 text-gray-700 space-y-1">
                        {valve.specs.map((spec, i) => (
                          <li key={i}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <p className="mt-4 text-gray-600 text-lg text-justify">
                      {valve.details}
                    </p>
                    
                    <div className="mt-8 flex justify-center">
                      <a
                        href="https://wa.me/+919819600149"
                        className="inline-block rounded bg-rose-600 px-8 py-3 text-md font-medium text-white transition hover:bg-rose-700 focus:outline-none focus:ring focus:ring-rose-300"
                      >
                        Request Quote
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ))}
      </div>

      {/* Oil & Gas Applications */}
      <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Valve Applications in Oil & Gas
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  {[
                      {
                          title: "Wellhead Control",
                          content: "Gate and ball valves for Christmas tree assemblies"
                      },
                      {
                          title: "Pipeline Isolation",
                          content: "Full-port ball valves for sectionalizing pipelines"
                      },
                      {
                          title: "Refinery Processing",
                          content: "Control valves for fractionation columns"
                      },
                      {
                          title: "Tank Farm Safety",
                          content: "Pressure relief valves for storage tanks"
                      },
                      {
                          title: "Compressor Stations",
                          content: "Check valves for anti-surge protection"
                      },
                      {
                          title: "Offshore Platforms",
                          content: "Cryogenic valves for LNG processing"
                      }
                  ].map((app, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">{app.title}</h3>
                          <p className="text-gray-700">{app.content}</p>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Enhanced Technical Support CTA */}
      <section className="py-16 bg-gradient-to-r from-rose-700 to-rose-900 text-white">
          <div className="container mx-auto px-4 text-center">
              <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                      Need Valve Engineering Support?
                  </h2>
                  <p className="text-xl mb-10">
                      Our API-certified engineers provide:
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-12">
                      {[
                          "Valve Selection & Sizing",
                          "Fugitive Emission Testing",
                          "Material Selection",
                          "Actuator Sizing",
                          "Hazardous Area Certification",
                          "Troubleshooting & Maintenance"
                      ].map((service, index) => (
                          <div key={index} className="flex items-start">
                              <HiOutlineBadgeCheck className="text-2xl mt-1 mr-3 text-rose-200 flex-shrink-0" />
                              <span>{service}</span>
                          </div>
                      ))}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <a
                          href="tel:+919819600149"
                          className="bg-white text-rose-700 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition duration-300"
                      >
                          Call: +91 9819600149
                      </a>
                      <a
                          href="mailto:info@oilandgasindustrialequipmentsupply.com"
                          className="border-2 border-white hover:bg-rose-800 font-bold py-3 px-8 rounded-lg transition duration-300"
                      >
                          Email Technical Team
                      </a>
                  </div>
              </div>
          </div>
      </section>

      {/* Project Case Studies */}
      <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Global Valve Projects
              </h2>
              
              <div className="max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8">
                      <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">Refinery Expansion Project</h3>
                          <p className="text-gray-700 mb-4">
                              <span className="font-semibold">Location:</span> Middle East<br />
                              <span className="font-semibold">Solution:</span> API 600 Gate Valves<br />
                              <span className="font-semibold">Quantity:</span> 800+ units<br />
                              <span className="font-semibold">Features:</span> Pressure Seal, Anti-Blowout Stem
                          </p>
                          <div className="flex items-center">
                              <FaCheck className="text-green-600 mr-2" />
                              <span>Zero leakage in fugitive emission tests</span>
                          </div>
                      </div>
                      
                      <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">LNG Terminal Valves</h3>
                          <p className="text-gray-700 mb-4">
                              <span className="font-semibold">Location:</span> Australia<br />
                              <span className="font-semibold">Solution:</span> Cryogenic Ball Valves<br />
                              <span className="font-semibold">Quantity:</span> 350+ units<br />
                              <span className="font-semibold">Features:</span> Extended Bonnet, Fire-Safe Design
                          </p>
                          <div className="flex items-center">
                              <FaCheck className="text-green-600 mr-2" />
                              <span>Operational at -196°C for 5+ years</span>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </section>
     
      <Footer />
    </>
  );
};

export default Valves;